import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ArrowUpRight,
  Upload,
  Palette,
  Sparkles,
  Download,
  Zap,
  Layers,
  ImageIcon,
  Wand2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

import { trackCTAClicked } from "@/lib/analytics";
import MarqueeBanner from "@/components/MarqueeBanner";
import { Seo } from "@/components/Seo";
import BeforeAfterCompare from "@/components/landing/BeforeAfterCompare";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import { toast } from "sonner";
import { isNativePlatform } from "@/lib/platform";
import { supabase } from "@/integrations/supabase/client";
import { useExploreShowcase, groupByRoom, type ShowcaseItem } from "@/hooks/useExploreShowcase";
import { DESIGN_CATEGORIES } from "@/data/designStyles";
import { ROOM_TYPES_DATA } from "@/data/roomTypes";
import MarqueeStrip from "@/components/landing/MarqueeStrip";
import Hero from "@/components/landing/Hero";
import BounceCards from "@/components/landing/BounceCards";
import RoomRefreshShowcase from "@/components/landing/RoomRefreshShowcase";

import heroFallback from "@/assets/hero-interior.jpg";
import heroBg from "@/assets/hero-bg.jpg";
import heroBefore from "@/assets/hero-before.jpg";
import pairBBefore from "@/assets/pair-b-before.jpg";
import pairBAfter from "@/assets/pair-b-after.jpg";
import heroFeature from "@/assets/hero-feature.png";

const logoIcon = "/images/dc-logo.webp";

import baJapandiBedroom from "@/assets/ba-japandi-bedroom.jpg";
import baScandiLiving from "@/assets/ba-scandi-living.jpg";
import baFarmhouseKitchen from "@/assets/ba-farmhouse-kitchen.jpg";
import baCoastalBathroom from "@/assets/ba-coastal-bathroom.jpg";
import baIndustrialOffice from "@/assets/ba-industrial-office.jpg";
import baMidcenturyDining from "@/assets/ba-midcentury-dining.jpg";

/** TODO: user will upload the paired "after" for the hero empty room.
 *  Once received, swap this constant to that image URL (asset import or CDN path). */
const HERO_AFTER_URL: string | null = null;

const EU_COUNTRIES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "NO",
  "CH",
  "IS",
  "LI",
]);

const isLikelyBot = (ua: string) =>
  /bot|crawl|spider|slurp|facebookexternalhit|embedly|preview|whatsapp|telegram/i.test(ua);

const EASE = [0.2, 0.8, 0.2, 1] as [number, number, number, number];

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: EASE },
};

/* ── Empty-room fallbacks (BEFORE frames) — real photographs of untouched rooms ── */
const EMPTY_ROOMS: Record<string, string> = {
  bedroom: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=1600&q=80&auto=format&fit=crop",
  "living room": "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1600&q=80&auto=format&fit=crop",
  living: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1600&q=80&auto=format&fit=crop",
  kitchen: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1600&q=80&auto=format&fit=crop",
  bathroom: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1600&q=80&auto=format&fit=crop",
  office: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=1600&q=80&auto=format&fit=crop",
  "home office": "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=1600&q=80&auto=format&fit=crop",
  "dining room": "https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=1600&q=80&auto=format&fit=crop",
  dining: "https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=1600&q=80&auto=format&fit=crop",
};

const SHOWCASE_ROOMS: { key: string; label: string }[] = [
  { key: "bedroom", label: "Bedroom" },
  { key: "living room", label: "Living Room" },
  { key: "kitchen", label: "Kitchen" },
  { key: "bathroom", label: "Bathroom" },
  { key: "home office", label: "Office" },
  { key: "dining room", label: "Dining Room" },
];

const POPULAR_STYLE_NAMES = [
  "Modern",
  "Japandi",
  "Luxury",
  "Scandinavian",
  "Minimalist",
  "Industrial",
  "Bohemian",
  "Contemporary",
  "Coastal",
  "Warm Minimalism",
];

/* Build a quick style→image lookup from the shipped designStyles catalog */
const STYLE_IMAGE_LOOKUP: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  DESIGN_CATEGORIES.forEach((c) =>
    c.styles.forEach((s) => {
      if (s.image) out[s.name.toLowerCase()] = s.image;
    }),
  );
  return out;
})();

const styleImage = (name: string, exploreItems: ShowcaseItem[]): string | undefined => {
  const lower = name.toLowerCase();
  const fromExplore = exploreItems.find((i) => (i.style || "").toLowerCase() === lower);
  if (fromExplore) return fromExplore.image;
  return STYLE_IMAGE_LOOKUP[lower];
};

/* ─────────────────────────────────────────────────────────────
   NAV
   ───────────────────────────────────────────────────────────── */
function EdNav({ onCTA }: { onCTA: () => void }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.05),0_4px_20px_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav
        className="mx-auto max-w-[1280px] px-6 lg:px-10 flex items-center justify-between gap-4 py-5"
        aria-label="Primary"
      >
        <Link to="/" aria-label="Decor Canvas — Home" className="flex items-center gap-2 shrink-0">
          <img
            src={logoIcon}
            alt="Decor Canvas"
            className="h-9 w-auto"
            loading="eager"
            decoding="async"
          />
          <span
            className="text-[18px] sm:text-[19px] font-semibold tracking-[-0.02em] text-[#111] transition-colors"
          >
            Decor Canvas
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {[
            { l: "Explore", to: "/explore" },
            { l: "Styles", to: "/styles" },
            { l: "Rooms", to: "/rooms" },
            { l: "Pricing", to: "/pricing" },
            { l: "Blogs", to: "/blog" },
          ].map((item) => (
            <Link
              key={item.l}
              to={item.to}
              className="text-[14px] font-medium text-[#333] hover:text-[color:var(--ed-coral)] transition-colors"
            >
              {item.l}
            </Link>
          ))}
        </div>

        <Button
          onClick={onCTA}
          size="sm"
          className="rounded-full h-10 px-5 bg-[color:var(--ed-coral)] hover:bg-[color:var(--ed-coral-deep)] text-white text-[13px] font-semibold transition-colors"
        >
          Start Designing
        </Button>
      </nav>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────
   HERO
   ───────────────────────────────────────────────────────────── */
function HeroSplit({
  onDesigner,
  onExplore,
  featured,
}: {
  onDesigner: () => void;
  onExplore: () => void;
  featured: ShowcaseItem | null;
}) {
  // Hero pair always uses the uploaded empty room as BEFORE.
  // AFTER prefers the user-supplied hero-after URL; falls back to the featured Explore image; then fallback asset.
  const beforeSrc = heroBefore;
  const afterSrc = HERO_AFTER_URL || featured?.image || heroFallback;

  // Parallax scroll for hero bg — gated to viewport, disabled for reduced motion.
  const heroRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = heroRef.current;
    const bg = bgRef.current;
    if (!el || !bg) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let visible = false;
    let raf = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
      },
      { threshold: 0 },
    );
    io.observe(el);

    const update = () => {
      raf = 0;
      if (!visible) return;
      const rect = el.getBoundingClientRect();
      // How far past the top of viewport (0 at top, negative as we scroll)
      const y = Math.max(-rect.height, Math.min(rect.height, -rect.top));
      bg.style.setProperty("--ed-parallax-y", `${y * 0.32}px`);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative pt-[112px] pb-16 sm:pt-32 sm:pb-24 overflow-hidden"
      aria-labelledby="hero-title"
    >
      {/* Parallax interior background */}
      <div ref={bgRef} className="ed-hero-parallax" style={{ backgroundImage: `url(${heroBg})` }} />
      {/* Legibility veil — soft warm wash that lets the room breathe through */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/78 via-white/55 to-white pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-white/60 via-transparent to-white/30 pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="col-span-12 lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e8e8e8] bg-[#f8f8f7] px-3 py-1.5 text-[12px] text-[#555] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ed-coral)]" />
              AI Interior Design — new
            </div>

            <h1
              id="hero-title"
              className="text-[40px] sm:text-[56px] lg:text-[64px] font-bold leading-[1.05] tracking-[-0.025em] text-[#111]"
            >
              AI Interior Design that lets you visualize before you renovate.
            </h1>

            <p className="mt-7 max-w-[560px] text-[17px] sm:text-[18px] leading-[1.7] text-[#555]">
              Upload a photo of your room, choose from over 90 interior styles, and instantly generate beautiful,
              photoreal room transformations — before you make a single real design decision.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                onClick={onDesigner}
                size="lg"
                className="h-12 px-6 rounded-full bg-[color:var(--ed-coral)] hover:bg-[color:var(--ed-coral-deep)] text-white text-[14px] font-medium shadow-none"
              >
                Start Designing
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                onClick={onExplore}
                size="lg"
                variant="outline"
                className="h-12 px-6 rounded-full border-[#e8e8e8] bg-white hover:bg-[#f8f8f7] text-[#111] text-[14px] font-medium shadow-none"
              >
                Explore Designs
              </Button>
            </div>

            <ul className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6 max-w-[560px] text-[13px] text-[#555]">
              {[
                { n: "90+", l: "Interior Styles" },
                { n: "23", l: "Room Types" },
                { n: "AI", l: "Powered" },
                { n: "12s", l: "Avg. Render" },
              ].map((t) => (
                <li key={t.l} className="border-l border-[#e8e8e8] pl-3">
                  <div className="text-[15px] font-semibold text-[#111] tracking-[-0.01em]">{t.n}</div>
                  <div className="text-[12px] text-[#888] mt-0.5">{t.l}</div>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
            className="col-span-12 lg:col-span-6"
          >
            <div className="relative rounded-[20px] border border-[#e8e8e8] bg-white p-2.5 shadow-[0_20px_60px_-30px_rgba(17,17,17,0.18)]">
              <div className="flex items-center justify-between px-2 pb-2">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#f5f5f4] border border-[#e8e8e8]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#f5f5f4] border border-[#e8e8e8]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#f5f5f4] border border-[#e8e8e8]" />
                </div>
                <div className="text-[11px] tracking-wider text-[#888] uppercase">decorcanvas.design / designer</div>
                <div className="text-[11px] text-[color:var(--ed-coral)] font-medium">Live</div>
              </div>
              <BeforeAfterCompare
                before={beforeSrc}
                after={afterSrc}
                beforeAlt="Original empty room before AI interior design"
                afterAlt={`${featured?.style || "Modern"} AI room transformation`}
                className="aspect-[4/3] w-full"
                rounded="rounded-[14px]"
                initial={52}
              />
              <div className="flex items-center justify-between px-2 pt-3 pb-1">
                <div className="text-[12px] text-[#555]">
                  {featured?.room ? (
                    <>
                      <span className="text-[#111] font-medium capitalize">{featured.room}</span>
                      <span className="mx-2 text-[#e8e8e8]">·</span>
                      <span className="capitalize">{featured.style}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-[#111] font-medium">Living Room</span>
                      <span className="mx-2 text-[#e8e8e8]">·</span>
                      <span>Warm Minimalism</span>
                    </>
                  )}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-[#888]">Drag to compare</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   FEATURE HIGHLIGHT — process
   ───────────────────────────────────────────────────────────── */
function FeatureHighlight({ heroImg }: { heroImg: string }) {
  const steps = [
    { icon: Upload, t: "Upload your room", d: "Any photo from your phone works — no floor plans, no measurements." },
    {
      icon: Palette,
      t: "Choose a style",
      d: "Pick from over 90 curated interior languages, or write your own prompt.",
    },
    { icon: Sparkles, t: "Generate in seconds", d: "Photoreal transformations rendered in about twelve seconds." },
    { icon: Download, t: "High-quality downloads", d: "Save, share, or iterate — your designs stay in your library." },
  ];
  return (
    <section className="py-20 sm:py-32 bg-[#f8f8f7] overflow-hidden" aria-labelledby="feature-title">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 sm:gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp} className="col-span-12 lg:col-span-6 ed-zoom rounded-[20px] border border-[#e8e8e8]">
            <img
              src={heroImg}
              alt="AI-generated interior design of a warm minimal living room"
              className="w-full h-auto aspect-[5/6] object-cover block rounded-[20px]"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
          <div className="col-span-12 lg:col-span-6 min-w-0">
            <motion.div {...fadeUp}>
              <div className="ed-caps text-[color:var(--ed-coral)] mb-4">The process</div>
              <h2
                id="feature-title"
                className="text-[26px] sm:text-[40px] font-bold leading-[1.15] tracking-[-0.02em] text-[#111] break-words"
              >
                From an empty room to a designed one. In four calm steps.
              </h2>
              <p className="mt-5 max-w-[560px] text-[15px] sm:text-[16px] leading-[1.7] text-[#555]">
                Decor Canvas is a design instrument, not a template gallery. Every generation respects your room&rsquo;s
                geometry, light, and materials.
              </p>
            </motion.div>

            <ol className="mt-8 sm:mt-10">
              {steps.map((s, i) => (
                <motion.li
                  key={s.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
                  className="border-t border-[#e8e8e8] py-5 last:border-b sm:grid sm:grid-cols-12 sm:gap-4 sm:items-start"
                >
                  <div className="flex items-center gap-3 sm:contents">
                    <div className="sm:col-span-1 text-[13px] font-mono text-[#888] sm:pt-1">0{i + 1}</div>
                    <div className="sm:col-span-1 sm:pt-0.5">
                      <s.icon className="w-5 h-5 text-[#111]" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="mt-2 sm:mt-0 sm:col-span-10 min-w-0 break-words">
                    <h3 className="text-[17px] font-semibold text-[#111] tracking-[-0.01em]">{s.t}</h3>
                    <p className="mt-1 text-[15px] leading-[1.6] text-[#555]">{s.d}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   BEFORE / AFTER SHOWCASE — horizontal rail
   ───────────────────────────────────────────────────────────── */
function BeforeAfterShowcase({ items }: { items: ShowcaseItem[] }) {
  const grouped = useMemo(() => groupByRoom(items), [items]);
  const railRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const rail = railRef.current;
    const track = trackRef.current;
    if (!rail || !track) return;
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    // Track holds two copies of the cards; one loop width = half the track width.
    let loopWidth = track.getBoundingClientRect().width / 2;
    const ro = new ResizeObserver(() => {
      loopWidth = track.getBoundingClientRect().width / 2;
    });
    ro.observe(track);

    rail.scrollLeft = 0;
    let pausedUntil = 0;
    let lastTs = performance.now();
    let rafId = 0;
    const speed = 60; // seconds per loop, matches previous CSS animation feel

    const bump = () => { pausedUntil = performance.now() + 1200; };
    const tick = (ts: number) => {
      const dt = ts - lastTs;
      lastTs = ts;
      if (!prefersReducedMotion && ts >= pausedUntil && loopWidth > 0) {
        let next = rail.scrollLeft + (loopWidth / (speed * 1000)) * dt;
        if (next >= loopWidth) next -= loopWidth;
        rail.scrollLeft = next;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    const onScroll = () => {
      if (performance.now() < pausedUntil && loopWidth > 0) {
        if (rail.scrollLeft >= loopWidth) rail.scrollLeft -= loopWidth;
        else if (rail.scrollLeft < 0) rail.scrollLeft += loopWidth;
      }
    };
    rail.addEventListener("mouseenter", bump);
    rail.addEventListener("pointerdown", bump);
    rail.addEventListener("touchstart", bump, { passive: true });
    rail.addEventListener("wheel", bump, { passive: true });
    rail.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      rail.removeEventListener("mouseenter", bump);
      rail.removeEventListener("pointerdown", bump);
      rail.removeEventListener("touchstart", bump);
      rail.removeEventListener("wheel", bump);
      rail.removeEventListener("scroll", onScroll);
    };
  }, []);


  // Same empty room as "before" for every card — "after" is that room restyled per pairing.
  const ROOM_STYLE_PAIRS: { label: string; style: string; after: string }[] = [
    { label: "Bedroom", style: "Japandi", after: baJapandiBedroom },
    { label: "Living Room", style: "Scandinavian", after: baScandiLiving },
    { label: "Kitchen", style: "Modern Farmhouse", after: baFarmhouseKitchen },
    { label: "Bathroom", style: "Coastal", after: baCoastalBathroom },
    { label: "Office", style: "Industrial", after: baIndustrialOffice },
    { label: "Dining Room", style: "Mid-Century Modern", after: baMidcenturyDining },
  ];

  const cards = ROOM_STYLE_PAIRS.map((p) => ({
    label: p.label,
    style: p.style,
    before: heroBefore,
    after: p.after,
  }));
  // Suppress unused warnings for legacy helpers retained for other sections.
  void grouped;
  void EMPTY_ROOMS;

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-white" aria-labelledby="ba-title">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div className="w-full sm:w-auto sm:flex-1">
            <div className="ed-caps text-[color:var(--ed-coral)] mb-3">Real transformations</div>
            <h2
              id="ba-title"
              className="text-[26px] sm:text-[32px] lg:text-[40px] font-bold leading-[1.15] tracking-[-0.02em] text-[#111] max-w-[720px]"
            >
              Before. After. Every room, redesigned by AI.
            </h2>
            <p className="mt-3 sm:mt-4 max-w-[620px] text-[15px] sm:text-[16px] leading-[1.7] text-[#555]">
              Drag the divider on any card. Every &lsquo;after&rsquo; below is a real Decor Canvas generation from the
              community — never a stock image.
            </p>
          </div>
          <Link to="/explore" className="w-full sm:w-auto text-[13px] text-[#111] ed-underline">
            See the full gallery →
          </Link>
        </div>
      </div>

      <div ref={railRef} className="relative overflow-x-auto overflow-y-hidden ba-marquee-mask ba-marquee-scroll">
        <div ref={trackRef} className="flex gap-4 sm:gap-5 w-max px-6 lg:px-0">

          {[...cards, ...cards].map((c, i) => (
            <article key={`${c.label}-${i}`} className="shrink-0 w-[82vw] sm:w-[520px] lg:w-[560px]">
              <div className="ed-card overflow-hidden">
                <BeforeAfterCompare
                  before={c.before}
                  after={c.after}
                  beforeAlt={`Empty ${c.label.toLowerCase()} before AI interior design`}
                  afterAlt={`${c.style} ${c.label} AI room makeover`}
                  className="aspect-[4/3] w-full"
                  rounded="rounded-none"
                  initial={50}
                />
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-t border-[#e8e8e8]">
                  <div>
                    <div className="ed-caps text-[#888]">{c.label}</div>
                    <div className="text-[14px] sm:text-[15px] text-[#111] font-medium mt-1 capitalize">{c.style}</div>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider text-[#888]">Drag ← →</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

  );
}

/* ─────────────────────────────────────────────────────────────
   POPULAR STYLES
   ───────────────────────────────────────────────────────────── */
function PopularStyles({ items }: { items: ShowcaseItem[] }) {
  const navigate = useNavigate();
  return (
    <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-[#f8f8f7]" aria-labelledby="styles-title">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="ed-caps text-[color:var(--ed-coral)] mb-3">The directory</div>
            <h2
              id="styles-title"
              className="text-[32px] sm:text-[40px] font-bold leading-[1.1] tracking-[-0.02em] text-[#111]"
            >
              Popular interior design styles.
            </h2>
          </div>
          <Link to="/styles" className="text-[13px] text-[#111] ed-underline">
            Browse all 90+ styles →
          </Link>
        </div>

        <MarqueeStrip
          className="mt-2 -mx-6 lg:-mx-10"
          speed={60}
          direction="left"
          scrollable
          items={POPULAR_STYLE_NAMES.map((name) => {
            const img = styleImage(name, items) || heroFallback;
            return (
              <button key={name} onClick={() => navigate("/styles")} className="group text-left">
                <div className="ed-zoom rounded-[14px] border border-[#e8e8e8] bg-white overflow-hidden">
                  <img
                    src={img}
                    alt={`${name} interior design style — AI room inspiration`}
                    className="w-[220px] aspect-[4/5] object-cover block"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="mt-3 text-center">
                  <h3 className="text-[14px] font-medium text-[#111] tracking-[-0.01em] ed-underline">{name}</h3>
                </div>
              </button>
            );
          })}
        />
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   WHY DECOR CANVAS
   ───────────────────────────────────────────────────────────── */
function WhyDecorCanvas() {
  const items = [
    { t: "AI Powered", d: "Create realistic room transformations using the latest AI technology." },
    { t: "Photorealistic Results", d: "Preserves geometry, light, and materials of your real room." },
    { t: "90+ Interior Styles", d: "From Japandi to Art Deco — hand-tuned, never generic." },
    { t: "Easy Workflow", d: "Upload, pick a style, generate. No learning curve." },
    { t: "Fast Rendering", d: "Create a beautiful AI-powered room design in around 12 seconds." },
    { t: "High-quality downloads", d: "Export your designs for sharing or renovation planning." },
  ];
  return (
    <section className="pt-12 sm:pt-16 pb-24 sm:pb-32 bg-[#faf8f5]" aria-labelledby="why-title">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[42%_58%] gap-16 lg:gap-24 items-start">
          {/* Left anchor */}
          <div className="lg:sticky lg:top-24">
            <span className="inline-block text-[11px] uppercase tracking-[0.22em] text-[color:var(--ed-coral)] mb-6 font-mono">
              Why Decor Canvas
            </span>
            <h2
              id="why-title"
              className="text-[44px] lg:text-[60px] font-light text-[#2d2d2d] leading-[1.05] tracking-[-0.02em]"
            >
              Built for people who love interiors —
              <br />
              <span className="italic font-serif text-[color:var(--ed-coral)]">and hate guesswork.</span>
            </h2>
            <p className="mt-8 text-[17px] text-[#2d2d2d]/70 leading-relaxed max-w-md">
              Photoreal room transformations that respect your space — geometry, light, and materials preserved, style
              refined.
            </p>

            <div className="mt-12 h-px w-24 bg-[#f0ebe3]" />
          </div>

          {/* Right feature list — staggered 2-col */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
            {items.map((f, i) => (
              <motion.div
                key={f.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, ease: EASE, delay: (i % 2) * 0.06 }}
                className={`group ${i % 2 === 1 ? "md:mt-12" : ""}`}
              >
                <div className="text-[10px] tracking-[0.15em] text-[color:var(--ed-coral)] mb-3 opacity-60 group-hover:opacity-100 transition-opacity font-mono">
                  {String(i + 1).padStart(2, "0")}
                </div>

                <h3 className="text-[20px] font-medium text-[#2d2d2d] mb-3 tracking-[-0.01em]">{f.t}</h3>
                <p className="text-[14px] text-[#2d2d2d]/60 leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   COMMUNITY WALL — masonry from Explore
   ───────────────────────────────────────────────────────────── */
function CommunityMasonry({ items }: { items: ShowcaseItem[] }) {
  const heroImages = items.slice(0, 6).map((it) => it.image);
  const transformStyles = [
    "rotate(8deg) translate(-355px)",
    "rotate(-4deg) translate(-215px)",
    "rotate(3deg) translate(-70px)",
    "rotate(-3deg) translate(80px)",
    "rotate(5deg) translate(225px)",
    "rotate(-6deg) translate(365px)",
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-[#f8f8f7]" aria-labelledby="community-title">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div className="w-full sm:w-auto sm:flex-1">
            <div className="ed-caps text-[color:var(--ed-coral)] mb-3">The community</div>
            <h2
              id="community-title"
              className="text-[26px] sm:text-[32px] lg:text-[40px] font-bold leading-[1.15] tracking-[-0.02em] text-[#111]"
            >
              Interior inspiration, made by Decor Community.
            </h2>
          </div>
          <Link to="/explore" className="w-full sm:w-auto text-[13px] text-[#111] ed-underline">
            Open Explore →
          </Link>
        </div>

        {heroImages.length === 6 && (
          <div className="w-full overflow-hidden flex justify-center h-[210px] sm:h-[290px] md:h-[345px] lg:h-[380px]">
            <div className="origin-top scale-[0.5] sm:scale-75 md:scale-90 lg:scale-100">
              <BounceCards
                images={heroImages}
                containerWidth={920}
                containerHeight={380}
                animationDelay={0.4}
                animationStagger={0.08}
                easeType="elastic.out(1, 0.6)"
                transformStyles={transformStyles}
                enableHover
              />
            </div>
          </div>
        )}
      </div>
    </section>

  );
}

/* ─────────────────────────────────────────────────────────────
   JOURNAL PREVIEW
   ───────────────────────────────────────────────────────────── */
function JournalPreview({ items }: { items: ShowcaseItem[] }) {
  const [posts, setPosts] = useState<
    Array<{
      id: string;
      title: string;
      slug: string;
      excerpt: string | null;
      cover_image_url: string | null;
      category_name: string | null;
    }>
  >([]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id,title,slug,excerpt,cover_image_url,blog_categories(name)")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(3);
      if (!active || !data) return;
      setPosts(
        (data as any[]).map((p) => ({
          id: p.id,
          title: p.title,
          slug: p.slug,
          excerpt: p.excerpt,
          cover_image_url: p.cover_image_url,
          category_name: p.blog_categories?.name ?? null,
        })),
      );
    })();
    return () => {
      active = false;
    };
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="py-24 sm:py-32 bg-white" aria-labelledby="journal-title">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="ed-caps text-[color:var(--ed-coral)] mb-3">FROM THE BLOGS</div>
            <h2
              id="journal-title"
              className="text-[32px] sm:text-[40px] font-bold leading-[1.1] tracking-[-0.02em] text-[#111]"
            >
              Ideas, trends, and interior stories.
            </h2>
          </div>
          <Link to="/blog" className="text-[13px] text-[#111] ed-underline">
            View All →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {posts.map((p, i) => {
            const img = p.cover_image_url || heroFallback;
            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, ease: EASE, delay: i * 0.06 }}
                className="group"
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  <div className="ed-zoom rounded-[16px] border border-[#e8e8e8] bg-white overflow-hidden">
                    <img
                      src={img}
                      alt={p.title}
                      className="w-full aspect-[4/3] object-cover block"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="mt-5">
                    {p.category_name && (
                      <span className="text-[11px] uppercase tracking-wider text-[color:var(--ed-coral)] font-medium">
                        {p.category_name}
                      </span>
                    )}
                    <h3 className="mt-2 text-[19px] font-semibold text-[#111] tracking-[-0.01em] leading-[1.35] group-hover:text-[color:var(--ed-coral-deep)] transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="mt-2 text-[14px] text-[#555] leading-[1.55] line-clamp-2">{p.excerpt}</p>
                    )}
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-[#555] ed-underline">
                      Read <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   FINAL CTA
   ───────────────────────────────────────────────────────────── */
function FinalCTA({ onDesigner, onExplore }: { onDesigner: () => void; onExplore: () => void }) {
  return (
    <section className="relative py-28 sm:py-40 bg-[#f8f8f7] overflow-hidden isolate" aria-labelledby="cta-title">
      {/* Floating rooms aesthetic background */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        {[
          {
            src: baJapandiBedroom,
            style: { top: "8%", left: "4%" },
            w: 200,
            rot: -6,
            anim: "ed-float-a",
            delay: "0s",
            blur: 0,
          },
          {
            src: baScandiLiving,
            style: { top: "62%", left: "2%" },
            w: 170,
            rot: 4,
            anim: "ed-float-b",
            delay: "1.4s",
            blur: 0.5,
          },
          {
            src: baFarmhouseKitchen,
            style: { top: "70%", left: "16%" },
            w: 140,
            rot: -3,
            anim: "ed-float-c",
            delay: "2.2s",
            blur: 0.6,
          },
          {
            src: baCoastalBathroom,
            style: { top: "6%", right: "6%" },
            w: 210,
            rot: 5,
            anim: "ed-float-b",
            delay: "0.6s",
            blur: 0,
          },
          {
            src: baIndustrialOffice,
            style: { top: "58%", right: "3%" },
            w: 180,
            rot: -4,
            anim: "ed-float-a",
            delay: "2s",
            blur: 0.4,
          },
          {
            src: baMidcenturyDining,
            style: { top: "72%", right: "18%" },
            w: 150,
            rot: 3,
            anim: "ed-float-c",
            delay: "1s",
            blur: 0.6,
          },
          {
            src: heroFallback,
            style: { top: "42%", left: "12%" },
            w: 120,
            rot: 6,
            anim: "ed-float-c",
            delay: "3s",
            blur: 0.8,
          },
          {
            src: heroBefore,
            style: { top: "40%", right: "12%" },
            w: 120,
            rot: -5,
            anim: "ed-float-b",
            delay: "2.6s",
            blur: 0.8,
          },
        ].map((c, i) => (
          <div
            key={i}
            className="absolute rounded-2xl overflow-hidden border border-white/60 shadow-[0_20px_50px_-20px_rgba(17,17,17,0.35)] opacity-80"
            style={{
              ...c.style,
              width: c.w,
              aspectRatio: "4 / 5",
              ["--ed-rot" as any]: `${c.rot}deg`,
              animation: `${c.anim} ${10 + (i % 3) * 2}s ease-in-out ${c.delay} infinite alternate`,
              filter: c.blur ? `blur(${c.blur}px)` : undefined,
            }}
          >
            <img src={c.src} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>

      {/* Warm scrim for legibility */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-b from-[#f8f8f7]/75 via-[#f8f8f7]/60 to-[#f8f8f7]/90"
      />
      {/* Center focus wash to keep headline crisp */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(45% 40% at 50% 45%, rgba(248,248,247,0.92), rgba(248,248,247,0.55) 55%, transparent 80%)",
        }}
      />
      {/* Coral radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background: "radial-gradient(60% 50% at 50% 40%, hsl(15 65% 55% / 0.10), transparent 70%)",
        }}
      />
      {/* Subtle vignette */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background: "radial-gradient(120% 80% at 50% 50%, transparent 60%, rgba(17,17,17,0.08) 100%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[880px] px-6 lg:px-10 text-center">
        <motion.h2
          {...fadeUp}
          id="cta-title"
          className="text-[36px] sm:text-[54px] font-bold leading-[1.05] tracking-[-0.025em] text-[#111]"
        >
          Ready to transform your room?
        </motion.h2>
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="mt-5 max-w-[560px] mx-auto text-[17px] leading-[1.7] text-[#555]"
        >
          Upload one photo. See ninety-plus styles rendered on your own walls. Free to start, on web, iOS and Android.
        </motion.p>
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <Button
            onClick={onDesigner}
            size="lg"
            className="h-12 px-6 rounded-full bg-[color:var(--ed-coral)] hover:bg-[color:var(--ed-coral-deep)] text-white text-[14px] font-medium shadow-lg shadow-[color:var(--ed-coral)]/20"
          >
            Start Designing
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <Button
            onClick={onExplore}
            size="lg"
            variant="outline"
            className="h-12 px-6 rounded-full border-[#e8e8e8] bg-white/90 backdrop-blur-sm hover:bg-white text-[#111] text-[14px] font-medium shadow-none"
          >
            Explore Designs
          </Button>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.22 }}
          className="mt-10 flex items-center justify-center gap-3 sm:gap-4"
        >
          <a
            href="https://apps.apple.com/in/app/decor-canvas/id6758079242"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Decor Canvas on the App Store"
            className="transition-transform hover:-translate-y-0.5"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              className="h-11 sm:h-12 w-auto"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.atheniaai.decorcanvas"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Decor Canvas on Google Play"
            className="transition-transform hover:-translate-y-0.5"
          >
            <img
              src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
              alt="Get it on Google Play"
              className="h-[62px] sm:h-[68px] w-auto -my-2"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   SEE IT IN ACTION — YouTube Shorts embed, editorial layout
   ───────────────────────────────────────────────────────────── */
function SeeItInAction() {
  return (
    <section className="relative bg-[#f8f8f7] py-24 sm:py-36 overflow-hidden" aria-labelledby="action-title">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #111 1px, transparent 1px), linear-gradient(to bottom, #111 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
          {/* Copy */}
          <motion.div {...fadeUp} className="col-span-12 lg:col-span-5 lg:sticky lg:top-32">
            <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#888]">
              <span className="text-[color:var(--ed-coral)]">02</span>
              <span className="h-px w-8 bg-[color:var(--ed-coral)]" />
              <span>In Motion</span>
            </div>
            <h2
              id="action-title"
              className="mt-6 text-[36px] sm:text-[52px] font-bold leading-[1.02] tracking-[-0.03em] text-[#111]"
            >
              See Decor Canvas
              <br />
              in <em className="not-italic text-[color:var(--ed-coral)]">motion</em>.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.75] text-[#555] max-w-[420px]">
              Watch a real room transform in seconds — no cuts, no filters. Just upload, pick a style, and let the
              render breathe life into empty walls.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Real render", "No cuts"].map((t) => (
                <span
                  key={t}
                  className="text-[11px] tracking-[0.14em] uppercase font-medium text-[#111] border border-[#e8e8e8] rounded-full px-3 py-1.5 bg-white"
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Video frame */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.15 }}
            className="col-span-12 lg:col-span-7 flex flex-col items-center"
          >
            <div className="relative w-full max-w-[380px] aspect-[9/16] rounded-[28px] overflow-hidden bg-black ring-1 ring-[color:var(--ed-coral)]/30 shadow-[0_40px_80px_-30px_rgba(17,17,17,0.35)]">
              <iframe
                src="https://www.youtube.com/embed/94ur84zv0YE?modestbranding=1&rel=0&playsinline=1"
                title="Decor Canvas in action"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
            <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.22em] text-[#888]">▶ YOUTUBE</div>
          </motion.div>

          {/* Landscape video — full width */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.2 }}
            className="col-span-12 flex flex-col items-center mt-10 lg:mt-16"
          >
            <div className="relative w-full max-w-[960px] aspect-video rounded-[28px] overflow-hidden bg-black ring-1 ring-[color:var(--ed-coral)]/30 shadow-[0_40px_80px_-30px_rgba(17,17,17,0.35)]">
              <iframe
                src="https://www.youtube.com/embed/ZFazOO_hU8A?modestbranding=1&rel=0&playsinline=1"
                title="Decor Canvas — full walkthrough"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
            <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.22em] text-[#888]">▶ YOUTUBE</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   FAQ SECTION
   ───────────────────────────────────────────────────────────── */
const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "What is Decor Canvas?",
    a: "Decor Canvas is an AI interior design app that lets you upload a photo of any room and instantly visualize it in 90+ design styles — including Scandinavian, Japandi, Indian Traditional, Punjabi Haveli, Vastu-aligned modern, Mid-Century Modern and more. The AI keeps your room's architecture intact while transforming furniture, color palette, and lighting.",
  },
  {
    q: "How accurate is the AI room visualization?",
    a: "Decor Canvas uses the latest AI image models, optimized to preserve the room’s structure while transforming furniture, finishes, lighting, and decor with realistic results.. Most users get usable, photorealistic concepts in under 30 seconds.",
  },
  {
    q: "Is Decor Canvas free?",
    a: "Yes. New users get free generations to try the app. You can upgrade to Pro for more credits, larger downloads, and watermark-free exports.",
  },
  {
    q: "Which room types are supported?",
    a: "23+ room types including living room, bedroom, kitchen, bathroom, kids' room, balcony, terrace, garden, home office, gym, gaming room, walk-in closet, cafes, retail stores and co-working spaces.",
  },
  {
    q: "Can I refresh my existing room without a full redesign?",
    a: "Yes — Room Refresh keeps your furniture, layout, windows, doors and architecture exactly as they are, and only changes the finishes: pick a new wall color, floor or tile style, or wallpaper (applied to all walls, tinted in your chosen color), rearrange existing furniture, or add items from your own photos. Available on web, iOS and Android.",
  },
  {
    q: "Does Decor Canvas work for Indian homes?",
    a: "Yes — Decor Canvas is built with Indian homes in mind. We offer regional Indian styles like Kerala Ethnic Minimalism, Punjabi Haveli Fusion, Goan Coastal Bungalow, Tamil Chettinad, Gujarati Haveli, Mumbai Art Deco, Vastu-aligned modern, and more.",
  },
  {
    q: "Is Decor Canvas available on Android and iOS?",
    a: "Yes — Decor Canvas is available as a web app and as native apps on Google Play (Android) and the App Store (iOS).",
  },
  {
    q: "Who builds Decor Canvas?",
    a: "Decor Canvas is built by Athenia AI Labs Private Limited, an India-based AI company focused on creative tools.",
  },
];

function FaqSection() {
  return (
    <section className="relative bg-white py-24 sm:py-36" aria-labelledby="faq-title">
      <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 lg:gap-14">
          <motion.div {...fadeUp} className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#888]">
              <span className="text-[color:var(--ed-coral)]">03</span>
              <span className="h-px w-8 bg-[color:var(--ed-coral)]" />
              <span>FREQUENTLY ASKED QUESTIONS</span>
            </div>
            <h2
              id="faq-title"
              className="mt-6 text-[36px] sm:text-[52px] font-bold leading-[1.02] tracking-[-0.03em] text-[#111]"
            >
              Questions,
              <br />
              <em className="not-italic text-[color:var(--ed-coral)]">answered</em>.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.75] text-[#555] max-w-[380px]">
              Still curious? We're one message away.
            </p>
            <Link
              to="/support"
              className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#111] border-b border-[color:var(--ed-coral)] pb-0.5 hover:text-[color:var(--ed-coral)] transition-colors"
            >
              Contact support <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>

          <motion.div {...fadeUp} className="col-span-12 lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {FAQ_ITEMS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-[#e8e8e8]">
                  <AccordionTrigger className="text-left text-[17px] sm:text-[19px] font-semibold text-[#111] hover:text-[color:var(--ed-coral)] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-[1.75] text-[#555] pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   LANDING
   ───────────────────────────────────────────────────────────── */
const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useReducedMotion();

  const { items: exploreItems } = useExploreShowcase(40);

  /* ─── payment return handler (preserved verbatim) ─── */
  useEffect(() => {
    const paymentId = searchParams.get("payment_id");
    const status = searchParams.get("status");
    if (paymentId) {
      if (status === "succeeded" || status === "completed") {
        toast.success("Payment successful! Your plan will be activated shortly.");
        navigate("/settings?fromPayment=true", { replace: true });
      } else if (status === "failed") {
        toast.error("Payment was not completed. Please try again.");
        window.history.replaceState({}, "", window.location.pathname);
      } else {
        navigate("/settings?fromPayment=true", { replace: true });
      }
    }
  }, [searchParams, navigate]);

  /* ─── geo-based region redirect (preserved verbatim) ─── */
  useEffect(() => {
    if (isNativePlatform()) return;
    if (window.location.pathname !== "/") return;
    if (window.location.search) return;
    try {
      if (sessionStorage.getItem("regionRedirected")) return;
    } catch {
      return;
    }
    if (typeof navigator !== "undefined" && isLikelyBot(navigator.userAgent || "")) return;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("detect-user-location");
        if (cancelled || error || !data) return;
        const code = (data as any).countryCode as string | undefined;
        if (!code) return;
        let target: string | null = null;
        if (code === "US") target = "/us";
        else if (code === "GB") target = "/uk";
        else if (EU_COUNTRIES.has(code)) target = "/eu";
        if (!target) return;
        try {
          sessionStorage.setItem("regionRedirected", "1");
        } catch {}
        navigate(target, { replace: true });
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  /* ─── load Geist font (Landing-scoped) ─── */
  useEffect(() => {
    const id = "ed-geist-fonts";
    if (document.getElementById(id)) return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap";
    document.head.appendChild(pre1);
    document.head.appendChild(pre2);
    document.head.appendChild(link);
  }, []);

  const goDesigner = (source: string) => {
    trackCTAClicked("start_designing", source);
    navigate("/designer");
  };
  const goExplore = (source: string) => {
    trackCTAClicked("explore_designs", source);
    navigate("/explore");
  };

  /* Pick a hero-worthy featured item — first Living Room or first item */
  const featured = useMemo<ShowcaseItem | null>(() => {
    if (!exploreItems.length) return null;
    const living = exploreItems.find((i) => (i.room || "").toLowerCase().includes("living"));
    return living || exploreItems[0];
  }, [exploreItems]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Decor Canvas",
    applicationCategory: "DesignApplication",
    operatingSystem: "Web, iOS, Android",
    description:
      "AI Interior Design app that transforms any room photo into 90+ interior styles, plus Room Refresh — the AI finish generator that repaints walls, swaps floors and tiles, and applies wallpaper while keeping your furniture and architecture.",
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", ratingCount: "1200" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };

  return (
    <div className="landing-root min-h-screen flex flex-col bg-white">
      <Seo
        title="AI Interior Design & Room Makeover in Seconds | Decor Canvas"
        description="Transform any room with AI Interior Design — upload one photo and visualize 90+ styles instantly. Or use Room Refresh: the AI finish generator that repaints walls, swaps floors and tiles, and applies wallpaper while keeping your furniture and architecture. Free on web, iOS and Android."
        canonicalPath="/"
        jsonLd={jsonLd}
      />

      <EdNav onCTA={() => goDesigner("nav")} />

      <MarqueeBanner />

      <main className="flex-1">
        <Hero onDesigner={() => goDesigner("hero")} onExplore={() => goExplore("hero")} />

        {/* Text marquee — design style names */}
        <div className="bg-white border-y border-[#e8e8e8] py-5">
          <MarqueeStrip
            className="ed-marquee-fade"
            speed={55}
            separator={<span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ed-coral)] inline-block" />}
            items={DESIGN_CATEGORIES.flatMap((c) => c.styles.slice(0, 3).map((s) => s.name))
              .slice(0, 18)
              .map((name) => (
                <span
                  key={name}
                  className="text-[22px] sm:text-[28px] font-semibold tracking-[-0.02em] text-[#111] whitespace-nowrap"
                >
                  {name}
                </span>
              ))}
          />
        </div>

        {/* Pair B — featured transformation, modern editorial asymmetric layout */}
        <section className="relative bg-white py-24 sm:py-36 overflow-hidden" aria-labelledby="pairb-title">
          {/* faint grid backdrop */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, #111 1px, transparent 1px), linear-gradient(to bottom, #111 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />
          <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10">
            <div className="grid grid-cols-12 gap-8 lg:gap-14 items-start">
              {/* Left column: editorial copy */}
              <motion.div {...fadeUp} className="col-span-12 lg:col-span-4 lg:sticky lg:top-32">
                <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#888]">
                  <span className="text-[color:var(--ed-coral)]">01</span>
                  <span className="h-px w-8 bg-[color:var(--ed-coral)]" />
                  <span>Transformation</span>
                </div>
                <h2
                  id="pairb-title"
                  className="mt-6 text-[36px] sm:text-[52px] font-bold leading-[1.02] tracking-[-0.03em] text-[#111]"
                >
                  From bare walls
                  <br />
                  to <em className="not-italic text-[color:var(--ed-coral)]">a room</em>
                  <br />
                  that breathes.
                </h2>
                <p className="mt-6 text-[15px] leading-[1.75] text-[#555] max-w-[380px]">
                  Same walls. Same window. Same floor. Every piece of furniture, every plant, every soft edge of light —
                  generated by Decor Canvas.
                </p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {["Japandi", "Living Room", "Warm Neutrals", "12s render"].map((t) => (
                    <span
                      key={t}
                      className="text-[11px] tracking-[0.14em] uppercase font-medium text-[#111] border border-[#e8e8e8] rounded-full px-3 py-1.5 bg-[#f8f8f7]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Right column: overlapping still image pair */}
              <div className="col-span-12 lg:col-span-8">
                <div className="relative aspect-[5/4] w-full">
                  {/* BEFORE — smaller, top-left, offset */}
                  <motion.figure
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="absolute top-0 left-0 w-[54%] aspect-[4/3] z-10"
                  >
                    <div className="relative w-full h-full rounded-[6px] overflow-hidden bg-[#f8f8f7] shadow-[0_20px_50px_-25px_rgba(17,17,17,0.25)]">
                      <img
                        src={pairBBefore}
                        alt="Empty modern room — before AI interior design"
                        className="w-full h-full object-cover block grayscale-[15%]"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-[10px] font-mono uppercase tracking-[0.22em] text-[#111] px-2.5 py-1 rounded-[3px]">
                        Before
                      </div>
                    </div>
                  </motion.figure>

                  {/* AFTER — dominant, bottom-right, overlapping */}
                  <motion.figure
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.85, ease: EASE, delay: 0.15 }}
                    className="absolute bottom-0 right-0 w-[72%] aspect-[16/11] z-20"
                  >
                    <div className="relative w-full h-full rounded-[6px] overflow-hidden bg-[#f8f8f7] ring-1 ring-[color:var(--ed-coral)]/40 shadow-[0_40px_80px_-30px_rgba(17,17,17,0.35)]">
                      <img
                        src={pairBAfter}
                        alt="Warm Japandi living room — generated by Decor Canvas"
                        className="w-full h-full object-cover block"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute top-3 left-3 bg-[color:var(--ed-coral)] text-white text-[10px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 rounded-[3px]">
                        After
                      </div>
                      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm text-[10px] font-mono uppercase tracking-[0.22em] text-[#111] px-2.5 py-1 rounded-[3px]">
                        Generated · 12s
                      </div>
                    </div>
                  </motion.figure>

                  {/* thin coral hairline connector, decorative */}
                  <div
                    aria-hidden
                    className="hidden lg:block absolute top-[38%] left-[48%] w-[12%] h-px bg-[color:var(--ed-coral)]/40 z-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <FeatureHighlight heroImg={heroFeature} />
        <BeforeAfterShowcase items={exploreItems} />
        <RoomRefreshShowcase onTry={() => goDesigner("room_refresh_section")} />
        <PopularStyles items={exploreItems} />

        <WhyDecorCanvas />
        <CommunityMasonry items={exploreItems} />
        <JournalPreview items={exploreItems} />

        <SeeItInAction />
        <FaqSection />

        {/* Text marquee — room types */}
        <div className="bg-[#f8f8f7] border-y border-[#e8e8e8] py-4">
          <MarqueeStrip
            className="ed-marquee-fade"
            speed={65}
            separator={<span className="text-[#e8e8e8]">/</span>}
            items={ROOM_TYPES_DATA.slice(0, 16).map((r) => (
              <span
                key={r.slug}
                className="text-[14px] uppercase tracking-[0.18em] font-medium text-[#555] whitespace-nowrap"
              >
                {r.name}
              </span>
            ))}
          />
        </div>

        <FinalCTA onDesigner={() => goDesigner("final-cta")} onExplore={() => goExplore("final-cta")} />
      </main>
    </div>
  );
};

export default Landing;
